import React, { useState } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../../OrbitContext';
import { TOrbit } from '../../i18n/TModules';
import { orbitTokens } from '../../styles';
import { ORBIT_L4_DEMO_FLOWS } from './fixtures';

const Root = styled.div`
  padding-top: 8px;
`;

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -6px 0 0 -6px;

  > * {
    margin: 6px 0 0 6px;
  }
`;

const Chip = styled.button`
  padding: 4px 10px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 999px;
  background: ${orbitTokens.surfaceAlt};
  color: ${orbitTokens.textSecondary};
  font-size: 11px;
  line-height: 15px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${orbitTokens.action};
    color: ${orbitTokens.text};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Toggle = styled.button`
  margin-bottom: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.action};
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
`;

/**
 * One prompt per demo flow under the newest turn, so every flow can be replayed in any order. Open
 * on the intro only; after that they fold away so the card the flow drew stays in view.
 */
const L4DemoPrompts: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking, turns } = useOrbitContext();
  const [open, setOpen] = useState(turns.length <= 1);

  return (
    <Root data-testid="orbit-l4-demo-prompts">
      <Toggle
        type="button"
        aria-expanded={open}
        data-testid="orbit-l4-demo-toggle"
        onClick={() => setOpen(value => !value)}
      >
        {open
          ? t(`${TOrbit}.layer4.demo.hideFlows`)
          : t(`${TOrbit}.layer4.demo.showFlows`, { count: ORBIT_L4_DEMO_FLOWS.length })}
      </Toggle>
      {open ? (
        <List>
          {ORBIT_L4_DEMO_FLOWS.map(flow => (
            <Chip
              key={flow.id}
              type="button"
              disabled={isAsking}
              data-testid={`orbit-l4-demo-prompt-${flow.id}`}
              onClick={() => {
                ask(flow.prompt).catch(() => undefined);
              }}
            >
              {flow.prompt}
            </Chip>
          ))}
        </List>
      ) : null}
    </Root>
  );
};

export default L4DemoPrompts;
